/**
 * SP 카탈로그 조회 도우미
 *
 * spCatalog.json 은 scripts/generateSpCatalog.js 가 코드에서 뽑아낸 "이 ERP가 실제로 부르는 SP 목록"이다.
 * 여기서는 그 목록을 AI가 쓰기 좋은 형태로 다듬는다.
 *   - findProcedure : 이름으로 정확히 찾기
 *   - searchProcedures : 키워드로 후보 찾기 (AI가 "미수 관련 SP 뭐 있어?" 하고 물을 때)
 *   - buildProcedureIndex : 시스템 프롬프트에 넣을 짧은 목록 (이름만)
 *
 * 손으로 고치고 싶은 SP는 SP_OVERRIDES 에 적으면 생성 결과보다 우선한다.
 * (생성기를 다시 돌려도 이 부분은 안 지워진다.)
 */

import rawCatalog from '../../config/spCatalog.json';

/**
 * 자동 추출이 놓쳤거나, 설명을 사람 말로 다듬고 싶은 SP를 여기에 적는다.
 * 형태는 카탈로그 항목과 동일하며 지정한 필드만 덮어쓴다.
 */
export const SP_OVERRIDES = {
  'DBO.ITN_S_상담리스트': {
    desc: '견적/상담 목록 조회. @구분은 "등록일"/"출발일", @상태 4는 전체를 뜻한다.',
  },
  'DBO.TRP_S_상담정보': {
    desc: '상담(견적) 1건의 상세 정보. @구분은 "U" 로 호출한다.',
  },
  'DBO.TRP_S_미수현황': {
    desc: '기간별 미수금(못 받은 돈) 현황 목록.',
  },
  'DBO.WSP_S_예약현황_02': {
    desc: '예약(주문) 현황 목록. @검색기준은 "출발일"/"등록일", 삭제/취소여부는 0=제외 1=포함.',
  },
  'DBO.TRP_S_투어상품리스트': {
    desc: '판매 중인 투어 상품 목록.',
  },
};

/** 이름 비교용 정규화: 대소문자와 DBO. 접두사 차이를 무시한다 */
const normalize = (name) =>
  String(name || '')
    .trim()
    .toUpperCase()
    .replace(/^DBO\./, '');

const catalog = rawCatalog.map((entry) => ({
  ...entry,
  ...(SP_OVERRIDES[entry.sp] || {}),
}));

const byName = new Map(catalog.map((e) => [normalize(e.sp), e]));

/** 전체 목록 */
export const getCatalog = () => catalog;

/** SP 이름으로 정확히 찾기 (DBO. 생략, 대소문자 무시 허용) */
export function findProcedure(name) {
  return byName.get(normalize(name)) || null;
}

/** 조회 SP 인지 (_S_) */
export const isQueryProcedure = (entry) => entry?.kind === 'S';

/**
 * 키워드로 SP 찾기.
 * 이름 > 설명 > 감싸는 함수명 순으로 점수를 매겨 상위 N개를 돌려준다.
 */
export function searchProcedures(keyword, { kind, limit = 12 } = {}) {
  const words = String(keyword || '')
    .split(/[\s,/]+/)
    .map((w) => w.trim().toLowerCase())
    .filter(Boolean);
  if (!words.length) return [];

  const scored = [];
  for (const entry of catalog) {
    if (kind && entry.kind !== kind) continue;
    const name = entry.sp.toLowerCase();
    const desc = String(entry.desc || '').toLowerCase();
    const fn = String(entry.fn || '').toLowerCase();
    let score = 0;
    for (const w of words) {
      if (name.includes(w)) score += 10;
      else if (desc.includes(w)) score += 4;
      else if (fn.includes(w)) score += 3;
    }
    if (score > 0) scored.push({ entry, score });
  }
  return scored
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ entry }) => describeProcedure(entry));
}

/** AI 에게 돌려줄 SP 설명 (JSON 으로 그대로 나간다) */
export function describeProcedure(entry) {
  if (!entry) return null;
  return {
    procedureName: entry.sp,
    종류: entry.kind === 'S' ? '조회' : '저장/수정/삭제',
    설명: entry.desc || '',
    파라미터: (entry.params || []).map((p) => ({
      이름: p.name,
      타입: p.type,
      ...(p.auto ? { 자동채움: '로그인 정보에서 자동으로 채워짐. 지정하지 않아도 된다.' } : {}),
      ...(p.sample ? { 예시값: p.sample } : {}),
    })),
  };
}

/**
 * 시스템 프롬프트에 넣을 SP 이름 목록.
 * 파라미터까지 넣으면 프롬프트가 10만 자를 넘으므로 이름만 넣고,
 * 자세한 파라미터는 search_procedures 툴로 그때그때 찾아보게 한다.
 */
export function buildProcedureIndex() {
  const group = (kind) =>
    catalog
      .filter((e) => e.kind === kind)
      .map((e) => e.sp.replace(/^DBO\./, ''))
      .join(', ');
  return `[조회 가능한 SP]\n${group('S')}\n\n[저장/수정/삭제 SP]\n${group('T')}`;
}
